import { BrowserStorage } from '@app/utils/BrowserStorage';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useSessionStorage<T>(key: string, initialValue: T) {
  const storage = useRef(new BrowserStorage('session')).current;
  const expirationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize state from storage or set initial value.
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = storage.get<T>(key);
    if (item !== null) {
      return item;
    }
    storage.set<T>(key, initialValue);
    return initialValue;
  });

  // Dispatch custom event to synchronize same-window instances.
  const dispatchSessionStorageEvent = useCallback(
    (newValue: T | null) => {
      window.dispatchEvent(new CustomEvent('session-storage-change', { detail: { key, newValue } }));
    },
    [key]
  );

  const setValue = useCallback(
    (value: T, expirationInMinutes?: number) => {
      if (expirationTimer.current) {
        clearTimeout(expirationTimer.current);
      }
      setStoredValue(value);
      storage.set<T>(key, value, expirationInMinutes);
      dispatchSessionStorageEvent(value);

      if (expirationInMinutes && expirationInMinutes > 0) {
        expirationTimer.current = setTimeout(() => {
          storage.remove(key);
          setStoredValue(initialValue);
          dispatchSessionStorageEvent(initialValue);
        }, expirationInMinutes * 60 * 1000);
      }
    },
    [dispatchSessionStorageEvent, initialValue, key, storage]
  );

  const removeValue = useCallback(() => {
    if (expirationTimer.current) {
      clearTimeout(expirationTimer.current);
    }
    storage.remove(key);
    setStoredValue(initialValue);
    dispatchSessionStorageEvent(initialValue);
  }, [dispatchSessionStorageEvent, initialValue, key, storage]);

  // Listen for native storage and custom events.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.storageArea === sessionStorage && e.key === key) {
        if (e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue);
            setStoredValue(parsed.value);
          } catch {
            // Ignore parse errors.
          }
        } else {
          setStoredValue(initialValue);
        }
      }
    };

    const handleCustomStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { key: eventKey, newValue } = customEvent.detail;
      if (eventKey === key) {
        setStoredValue(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('session-storage-change', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('session-storage-change', handleCustomStorageChange);
    };
  }, [initialValue, key]);

  // Cleanup expiration timer on unmount to avoid stray state updates.
  useEffect(() => {
    return () => {
      if (expirationTimer.current) {
        clearTimeout(expirationTimer.current);
      }
    };
  }, []);

  return { storedValue, setValue, removeValue };
}
